// src/ApprovalMatrix.js
import React, { useEffect, useState } from 'react';
import api from './apiClient'; // Use centralized client
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack'; // For toasts

function ApprovalMatrix({ workspaceId, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]); // [{ user_id, approver_ids: [...] }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  // Load data
  useEffect(() => {
    if (!workspaceId || !currentUserId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        setSuccessMsg('');

        const usersRes = await api.get('/api/workspace-users', {
          params: { workspace_id: workspaceId },
        });
        const approversRes = await api.get('/api/workspace-approvers', {
          params: { workspace_id: workspaceId },
        });

        const usersList = usersRes.data || [];
        setUsers(usersList);

        const map = {};
        (approversRes.data || []).forEach((row) => {
          const uId = row.user_id;
          const aId = row.approver_user_id;
          if (!map[uId]) map[uId] = [];
          if (!map[uId].includes(aId)) map[uId].push(aId);
        });

        const initialRows = usersList.map((u) => ({
          user_id: u.id,
          approver_ids: map[u.id] || [],
        }));

        setRows(initialRows);
      } catch (err) {
        console.error('Error loading approval matrix:', err);
        setError(err.response?.data?.error || 'Failed to load approval matrix data.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [workspaceId, currentUserId]);

  const handleChangeApprovers = (userId, newApproverIds) => {
    setRows((prev) =>
      prev.map((r) =>
        r.user_id === userId ? { ...r, approver_ids: newApproverIds } : r
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMsg('');

      const payload = {
        workspace_id: workspaceId,
        acting_user_id: currentUserId,
        mappings: rows.map((r) => ({
          user_id: r.user_id,
          approver_ids: r.approver_ids,
        })),
      };

      await api.post('/api/workspace-approvers', payload);
      setSuccessMsg('Approval matrix saved successfully.');
      enqueueSnackbar('Saved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving approval matrix:', err);
      const msg = err.response?.data?.error || 'Failed to save approval matrix.';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!workspaceId) {
    return (
      <Alert severity="warning">
        No workspace selected. Please select a workspace first.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Work Done – Approval Matrix
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Box sx={{ mb: 1 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {successMsg && (
            <Box sx={{ mb: 1 }}>
              <Alert severity="success">{successMsg}</Alert>
            </Box>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Approver(s)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const user = users.find((u) => u.id === row.user_id);
                if (!user) return null;

                return (
                  <TableRow key={row.user_id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      <Select
                        multiple
                        size="small"
                        value={row.approver_ids}
                        onChange={(e) =>
                          handleChangeApprovers(row.user_id, e.target.value)
                        }
                        renderValue={(selected) => {
                          const names = users
                            .filter((u) => selected.includes(u.id))
                            .map((u) => u.username);
                          return names.join(', ');
                        }}
                        sx={{ minWidth: 220 }}
                      >
                        {users.map((u) => (
                          <MenuItem
                            key={u.id}
                            value={u.id}
                            disabled={u.id === row.user_id} // cannot approve own logs
                          >
                            <Checkbox
                              checked={row.approver_ids.includes(u.id)}
                            />
                            <ListItemText primary={u.username} />
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Approval Matrix'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ApprovalMatrix;